import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTelegram } from "../../hooks/useTelegram";
import { useAuth } from "../../context/AuthContext";
import { useNotification } from "../../context/NotificationContext";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Send, Mail } from "lucide-react";
import TermsAcceptance, {
  allTermsAccepted,
  validateTermsAcceptance,
} from "../../components/legal/TermsAcceptance";
import { glassCard } from "../../utils/glassStyles";

export default function TelegramWelcome() {
  const { initData } = useTelegram();
  const { loginWithTelegram } = useAuth();
  const { add } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [termsValues, setTermsValues] = useState({
    acceptTerms: false,
    acceptPrivacy: false,
    acceptCookies: false,
  });
  const [termsErrors, setTermsErrors] = useState({});

  const handleTermsChange = (key, checked) => {
    setTermsValues((prev) => ({ ...prev, [key]: checked }));
    setTermsErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const redirectAfterLogin = (user) => {
    if (user?.termsAccepted === false) {
      const from = location.state?.from || "/applier/dashboard";
      navigate("/accept-policies", { replace: true, state: { from } });
      return;
    }

    const deepLink = sessionStorage.getItem("telegram_deep_link");
    if (deepLink) {
      sessionStorage.removeItem("telegram_deep_link");
      navigate(deepLink, { replace: true });
      return;
    }

    const from = location.state?.from || "/applier/dashboard";
    navigate(from, { replace: true });
  };

  const handleTelegramLogin = async () => {
    const policyErrors = validateTermsAcceptance(termsValues);
    if (Object.keys(policyErrors).length > 0) {
      setTermsErrors(policyErrors);
      add("Please accept all policies before continuing.", "error");
      return;
    }

    setLoading(true);
    try {
      const user = await loginWithTelegram(initData, termsValues);
      if (user) {
        redirectAfterLogin(user);
      }
    } catch (error) {
      add(error.response?.data?.message || "Login failed", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className={`w-full max-w-md ${glassCard}`}>
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">Welcome to Shebeka</CardTitle>
          <CardDescription>
            Find your dream job or hire top talent directly from Telegram.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <TermsAcceptance
            values={termsValues}
            onChange={handleTermsChange}
            errors={termsErrors}
          />

          <Button
            className="w-full bg-primary hover:bg-primary/90 text-white h-12 text-lg"
            onClick={handleTelegramLogin}
            disabled={loading || !allTermsAccepted(termsValues)}
          >
            <Send className="mr-2 h-5 w-5" />
            {loading ? "Logging in..." : "Continue with Telegram"}
          </Button>

          <div className="relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t after:border-border">
            <span className="relative z-10 px-2 text-muted-foreground bg-background">OR</span>
          </div>

          <Button variant="outline" className="w-full h-12" onClick={() => navigate("/login")}>
            <Mail className="mr-2 h-5 w-5" />
            Login with Email
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
